import apiClient from './api';
import { generatePromptPayQR } from '../utils/promptpayQR';

export interface DriverTip {
  id: string;
  delivery_id: string;
  driver_id: string;
  user_id: string;
  amount: number;
  promptpay_account: string;
  qr_code_data?: string;
  status: 'pending' | 'completed' | 'failed';
  paid_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTipRequest {
  delivery_id: string;
  driver_id: string;
  amount: number;
  promptpay_account: string;
}

export interface TipQRCodeData {
  qr_code_data: string;
  promptpay_account: string;
  amount: number;
}

class TipService {
  /**
   * Create a new tip for a driver
   */
  async createTip(data: CreateTipRequest): Promise<DriverTip> {
    try {
      // Generate PromptPay QR code data
      const qr_code_data = generatePromptPayQR(data.promptpay_account, {
        amount: data.amount
      });

      const response = await apiClient.post<{ data: DriverTip }>('/tips', {
        ...data,
        qr_code_data
      });

      return response.data.data;
    } catch (error) {
      console.error('Error creating tip:', error);
      throw error;
    }
  }

  /**
   * Get tip QR code data
   */
  async getTipQRCode(tipId: string): Promise<TipQRCodeData> {
    try {
      const response = await apiClient.get<{ data: TipQRCodeData }>(`/tips/${tipId}/qr`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting tip QR code:', error);
      throw error;
    }
  }

  /**
   * Mark tip as completed
   */
  async completeTip(tipId: string): Promise<DriverTip> {
    try {
      const response = await apiClient.post<{ data: DriverTip }>(`/tips/${tipId}/complete`);
      return response.data.data;
    } catch (error) {
      console.error('Error completing tip:', error);
      throw error;
    }
  }

  /**
   * Get tips for a specific delivery
   */
  async getTipsByDelivery(deliveryId: string): Promise<DriverTip[]> {
    try {
      const response = await apiClient.get<{ data: DriverTip[] }>(`/deliveries/${deliveryId}/tips`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting tips for delivery:', error);
      throw error;
    }
  }

  /**
   * Get tips by user
   */
  async getTipsByUser(): Promise<DriverTip[]> {
    try {
      const response = await apiClient.get<{ data: DriverTip[] }>('/tips/user');
      return response.data.data;
    } catch (error) {
      console.error('Error getting user tips:', error);
      throw error;
    }
  }

  /**
   * Check if user has already tipped for a delivery
   */
  async hasUserTipped(deliveryId: string): Promise<boolean> {
    try {
      const tips = await this.getTipsByDelivery(deliveryId);
      return tips.length > 0;
    } catch (error) {
      console.error('Error checking if user has tipped:', error);
      return false;
    }
  }
}

export const tipService = new TipService(); 